import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  type MenuTextMatch,
} from "@lexical/react/LexicalTypeaheadMenuPlugin";
import { Box, Button, InputBase, Typography, useTheme } from "@mui/material";
import { type LexicalNode, type TextNode } from "lexical";
import { type ReactElement, useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { $createMergeTagNode } from "./MergeTagNode";
import {
  $ancestorProductBlock,
  $ancestorProductContext,
  $insertProductBlockInContext,
  $insertProductContextAtCaret,
  $productContextBase,
} from "./ProductBlockPlugin";
import { useCaretInDiscoverContext } from "./useCaretInDiscoverContext";
import { useDefaultProductContextModel } from "./useDefaultProductContextModel";
import {
  getMergeTagByIdentifier,
  textComposerMergeTagCategories,
  type TextComposerMergeTagCategory,
} from "../mergeTagDefinitions";
import { DISCOVER_HEADING_TAG } from "../productContexts";
import {
  parseProductItemIdentifier,
  productItemIdentifier,
} from "../productItemTags";
import { useTextComposerMergeTagCategories } from "../useConnectorMergeTags";
import type { MergeTagCategory, ResolvedFilters } from "@/services/mergeTags";

import { SearchIcon } from "@/icons";

const MENU_TRIGGER_REGEX = /(?:^|\s|\()\{\{([^{}]*)$/;

function checkForMergeTagMatch(text: string): MenuTextMatch | null {
  const match = MENU_TRIGGER_REGEX.exec(text);
  if (!match) return null;

  const fullMatchLength = match[0].length;
  const queryLength = match[1].length;
  const leadingChars = fullMatchLength - queryLength - 2;

  return {
    leadOffset: match.index + leadingChars,
    matchingString: match[1],
    replaceableString: match[0].trimStart(),
  };
}

class MergeTagOption extends MenuOption {
  readonly identifier: string;
  readonly label: string;
  readonly tag: string;
  readonly categoryLabel: string;
  readonly defaultFilters?: ResolvedFilters;

  constructor(
    identifier: string,
    label: string,
    tag: string,
    categoryLabel: string,
    defaultFilters?: ResolvedFilters,
  ) {
    super(identifier);
    this.identifier = identifier;
    this.label = label;
    this.tag = tag;
    this.categoryLabel = categoryLabel;
    this.defaultFilters = defaultFilters;
  }
}

function buildAllOptions(
  categories: TextComposerMergeTagCategory[],
): MergeTagOption[] {
  const options: MergeTagOption[] = [];
  for (const category of categories) {
    for (const tag of category.tags) {
      const identifier = tag.tag.replace(/^\{\{|\}\}$/g, "").trim();
      options.push(
        new MergeTagOption(
          identifier,
          tag.label,
          tag.tag,
          category.label,
          tag.defaultFilters,
        ),
      );
    }
  }
  return options;
}

function buildOptionsFromCatalog(
  categories: MergeTagCategory[],
): MergeTagOption[] {
  const options: MergeTagOption[] = [];
  for (const category of categories) {
    for (const tag of category.tags) {
      options.push(
        new MergeTagOption(
          tag.identifier,
          tag.label,
          `{{${tag.identifier}}}`,
          category.label,
          tag.defaultFilters,
        ),
      );
    }
  }
  return options;
}

interface MergeTagTypeaheadPluginProps {
  /**
   * Resolved catalog categories to suggest (already surface-filtered + narrowed
   * by the host). When omitted, falls back to Text Composer picker categories
   * (including installed-connector metadata when a site is in route context).
   */
  categories?: MergeTagCategory[];
  /**
   * Notified when the suggestion menu opens (`true`) or closes (`false`). Hosts
   * that intercept keys (e.g. a single-line editor swallowing Enter) use this to
   * yield Enter-to-select to the menu while it is open.
   */
  onOpenChange?: (open: boolean) => void;
}

const MAX_SUGGESTIONS = 10;

export function MergeTagTypeaheadPlugin({
  categories,
  onOpenChange,
}: MergeTagTypeaheadPluginProps = {}): ReactElement | null {
  // Only load/sync composer connector categories when the host did not pass a
  // resolved catalog — otherwise the sync would wipe prop-only registry tags.
  if (categories !== undefined) {
    return (
      <MergeTagTypeaheadMenu
        categories={categories}
        onOpenChange={onOpenChange}
      />
    );
  }
  return <MergeTagTypeaheadFromComposer onOpenChange={onOpenChange} />;
}

function MergeTagTypeaheadFromComposer({
  onOpenChange,
}: {
  onOpenChange?: (open: boolean) => void;
}): ReactElement | null {
  const composerCategories = useTextComposerMergeTagCategories();
  const resolved =
    composerCategories.length > 0
      ? composerCategories
      : textComposerMergeTagCategories;
  return (
    <MergeTagTypeaheadMenu
      categories={undefined}
      composerCategories={resolved}
      onOpenChange={onOpenChange}
    />
  );
}

function MergeTagTypeaheadMenu({
  categories,
  composerCategories,
  onOpenChange,
}: {
  categories?: MergeTagCategory[];
  composerCategories?: TextComposerMergeTagCategory[];
  onOpenChange?: (open: boolean) => void;
}): ReactElement | null {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState<string | null>(null);
  const [menuSearch, setMenuSearch] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const theme = useTheme();
  const defaultContextModel = useDefaultProductContextModel();
  const inDiscover = useCaretInDiscoverContext();

  const allOptions = useMemo(() => {
    const options =
      categories !== undefined
        ? buildOptionsFromCatalog(categories)
        : buildAllOptions(composerCategories ?? textComposerMergeTagCategories);
    // The Discover headline only resolves inside a Discover section.
    return inDiscover
      ? options
      : options.filter((opt) => opt.identifier !== DISCOVER_HEADING_TAG);
  }, [categories, composerCategories, inDiscover]);

  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const opt of allOptions) {
      counts.set(opt.categoryLabel, (counts.get(opt.categoryLabel) ?? 0) + 1);
    }
    return counts;
  }, [allOptions]);

  // `menuSearch` stays null until the author types in the search box; from
  // then on it wins over the typed `{{query`, including when they clear it
  // back to empty — that means "show everything", not "mirror the editor".
  const effectiveQuery = menuSearch ?? queryString;

  const filteredOptions = useMemo(() => {
    if (effectiveQuery === null) return [];

    const byCategory =
      activeCategory === null
        ? allOptions
        : allOptions.filter((opt) => opt.categoryLabel === activeCategory);

    // Empty query: show the whole (category-scoped) list; the menu scrolls.
    if (effectiveQuery === "") return byCategory;

    const query = effectiveQuery.toLowerCase().replace(/\s+/g, "");
    return byCategory
      .filter(
        (opt) =>
          opt.label.toLowerCase().replace(/\s+/g, "").includes(query) ||
          opt.identifier.toLowerCase().includes(query),
      )
      .slice(0, MAX_SUGGESTIONS);
  }, [effectiveQuery, allOptions, activeCategory]);

  const onSelectOption = useCallback(
    (
      selectedOption: MergeTagOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void,
    ) => {
      editor.update(() => {
        // Product tags always land in a context: inside a block the chip
        // reads the block's product; inside context copy the context's first
        // item; anywhere else a fresh context wraps it.
        const item = parseProductItemIdentifier(selectedOption.identifier);
        if (item) {
          const block = $ancestorProductBlock(nodeToReplace);
          const context = block ? null : $ancestorProductContext(nodeToReplace);
          if (!block && !context) {
            nodeToReplace?.remove();
            $insertProductContextAtCaret(
              defaultContextModel,
              $createMergeTagNode(
                selectedOption.identifier,
                selectedOption.label,
                selectedOption.defaultFilters,
              ),
            );
            closeMenu();
            return;
          }
          const index = block
            ? block.getIndex()
            : $productContextBase(context!);
          const identifier = productItemIdentifier(index, item.field);
          const entry = getMergeTagByIdentifier(identifier);
          const chip = $createMergeTagNode(
            entry?.identifier ?? identifier,
            entry?.label ?? selectedOption.label,
            selectedOption.defaultFilters,
          );
          if (block) {
            nodeToReplace?.replace(chip);
            chip.selectNext();
            closeMenu();
            return;
          }
          // Inside a context but not a block: wrap the chip in its own guarded
          // block, mirroring the fresh-context path — never a loose chip.
          let line: LexicalNode | null = nodeToReplace;
          while (line !== null && !context!.is(line.getParent())) {
            line = line.getParent();
          }
          nodeToReplace?.remove();
          $insertProductBlockInContext(context!, line, chip);
          closeMenu();
          return;
        }

        const mergeTagNode = $createMergeTagNode(
          selectedOption.identifier,
          selectedOption.label,
          selectedOption.defaultFilters,
        );
        if (nodeToReplace) {
          nodeToReplace.replace(mergeTagNode);
        }
        mergeTagNode.selectNext();
        closeMenu();
      });
    },
    [editor, defaultContextModel],
  );

  const triggerFn = useCallback(
    (text: string) => checkForMergeTagMatch(text),
    [],
  );

  return (
    <LexicalTypeaheadMenuPlugin<MergeTagOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={triggerFn}
      onOpen={() => onOpenChange?.(true)}
      onClose={() => {
        setMenuSearch(null);
        setActiveCategory(null);
        onOpenChange?.(false);
      }}
      options={filteredOptions}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp },
      ) => {
        if (!anchorElementRef.current) {
          return null;
        }

        return createPortal(
          <Box
            sx={{
              bgcolor: "background.paper",
              borderRadius: `${theme.shape.borderRadius}px`,
              boxShadow: theme.shadows[3],
              py: 2,
              minWidth: 360,
              maxWidth: 460,
              maxHeight: 240,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            <Box sx={{ px: 3, pb: 2 }}>
              {/* Not autofocused: the caret stays in the editor so typing
              after `{{` keeps filtering. The box mirrors that typed query and
              takes over only when the author clicks into it. */}
              <InputBase
                value={effectiveQuery ?? ""}
                onChange={(e) => setMenuSearch(e.target.value)}
                placeholder="Search merge tags"
                fullWidth
                startAdornment={
                  <SearchIcon
                    fontSize="small"
                    sx={{ mr: 2, color: "text.secondary" }}
                  />
                }
                sx={{
                  px: 3,
                  py: 3,
                  fontSize: theme.typography.body1.fontSize,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: `${theme.shape.borderRadius}px`,
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                px: 3,
                pt: 2,
                pb: 2,
                overflowX: "auto",
                // Hide the horizontal scrollbar; the row stays swipeable.
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {[...categoryCounts].map(([label, count]) => (
                <Button
                  key={label}
                  size="small"
                  variant={activeCategory === label ? "contained" : "outlined"}
                  // Keep focus in the editor so the author can keep typing.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() =>
                    setActiveCategory((cur) => (cur === label ? null : label))
                  }
                  sx={{
                    flexShrink: 0,
                    borderRadius: 1,
                    px: 2,
                    py: 2,
                    minWidth: 0,
                  }}
                >
                  {label} ({count})
                </Button>
              ))}
            </Box>
            {filteredOptions.length === 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", px: 3, py: 2 }}
              >
                No matches
              </Typography>
            )}
            {filteredOptions.map((option, index) => {
              const showCategory =
                index === 0 ||
                filteredOptions[index - 1].categoryLabel !==
                  option.categoryLabel;
              return (
                <Box key={option.key}>
                  {showCategory && (
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        px: 3,
                        pt: index === 0 ? 0 : 2,
                        borderTop:
                          index === 0
                            ? "none"
                            : `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {option.categoryLabel}
                    </Typography>
                  )}
                  <Box
                    ref={(el: HTMLElement | null) => option.setRefElement(el)}
                    role="option"
                    aria-selected={selectedIndex === index}
                    onClick={() => selectOptionAndCleanUp(option)}
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      gap: 2,
                      px: 3,
                      py: 3,
                      cursor: "pointer",
                      bgcolor:
                        selectedIndex === index
                          ? "action.hover"
                          : "background.paper",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.label}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {option.tag}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>,
          anchorElementRef.current,
        );
      }}
    />
  );
}
